import React from 'react';
import {Set as ImmSet} from 'immutable';
import {CanvasBodyWithDefaults} from './components/CanvasBody';
import {CanvasHead} from './components/CanvasHead';
import {
  TypeKitFontsScript,
  CanvasLocalStyles,
  GoogleFontsSheet,
  useReceivedProjectData,
  CustomFonts,
  CanvasDefaultStyles,
  HtmlFragment,
  ExternalCustomCode,
  Script,
  CurrentPageCustomCode,
  telemetrySendError,
  buildCanvasAssetMap,
  OpenCanvasProvider,
} from '@packages/systems/designer/canvas';
import {getDesignerAppOrigin} from '@packages/utilities/canvas-origin';
import {rewriteViewportUnitsInHtml} from '@packages/systems/open-canvas/rewrite-viewport-units';
import noop from 'lodash/noop';
import {initBootstrapPlugins} from '@packages/systems/designer/plugins';
import {NormalizedAtomConfig, Plugins} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {QualifiedMap} from '@packages/systems/wfdl-Qualified';
import {
  ViewportEventHandlers,
  ViewportStyles,
} from '@packages/systems/open-canvas/ui';
import {canApplyZoomFactor, Point} from '@packages/systems/open-canvas/core';
import {
  CanvasViewportEventTrigger,
  type ContinuousViewportChangeActionType,
  canvasViewportChangeEnded,
  canvasViewportChangeStarted,
  canvasViewportPanned,
  canvasViewportZoomed,
  isPanTrigger,
} from '@packages/systems/open-canvas/store';
import {
  stringifyIx3ElementTargets,
  defaultStringifiedTargets,
} from '@packages/systems/ix3/shared';
import {getIx2NodeMap} from '@packages/shared/utils/ix2NodeUtils';
import {useReportReceivedRequiredProjectData} from './hooks/useReportReceivedProjectData';
import {useInitializeIX2OnPage} from './hooks/useInitializeIX2OnPage';
import {useDesignModeHandler} from './hooks/useDesignModeHandler';
import {useDynamoData} from './hooks/useDynamoData';
import {ErrorBoundary} from './components/ErrorBoundary';
import {getIXPreviewStyles} from '@packages/systems/designer/ix';
import {useReportCanvasLoadedWithWhiteScreen} from './hooks/useReportCanvasLoadedWithWhiteScreen';
import {useReportCustomCodeErrors} from './hooks/useReportCustomCodeErrors';
import {useResizeObserver} from './hooks/useResizeObserver';
import {DynamoDataProvider} from '@packages/systems/dynamo/graphql';
import {getId} from '@packages/systems/core/utils/RecordUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {setCanvasAppReady} from './utils/setCanvasAppReady';
import {dispatchInDesigner} from './utils/dispatchInDesigner';
import {CANVAS_APP_NODE_HOVERED} from '@packages/systems/designer/actions';
import {BodyEndCustomCode} from './components/BodyEndCustomCode';
import {useVariationAttributes} from './hooks/useVariationAttributes';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  DocumentEvents,
  useAreAllScriptsLoaded,
} from './components/DocumentEvents';
import {overrideDocumentWrite} from './utils/overrideDocumentWrite';
import {patchReactPinResilience} from './utils/patchReactPinResilience';
import {useDocumentAttributes} from './hooks/useDocumentAttributes';
import {useBaseSiteModules} from './hooks/useBaseSiteModules';
import {useEnsureNewDocumentOnPageSwitch} from './hooks/useEnsureNewDocumentOnPageSwitch';
import {useReportCanvasLatency} from './hooks/useReportCanvasLatency';
import {useNotifyDesignerAppCanvasAppUnload} from './hooks/useNotifyDesignerAppCanvasAppUnload';
import {buildClassNameMap} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
// See epic https://webflow.atlassian.net/browse/DESUX-8149 to pay off this 👇 tech debt
/* eslint-disable webflow/package-boundaries */
import CanvasStyleSheet from '../../../public/js/designer-flux/LegacyStyleSystem/components/CanvasStyleSheet';
import {buildStylesMapForCanvasApp} from '../../../public/js/designer-flux/LegacyStyleSystem/utils/StyleUtils';
import {useInitializeIX3OnSeparateCanvasOrigin} from '../../../public/js/designer-flux/IX3/hooks/useInitializeIX3OnSeparateCanvasOrigin';
import {parseCommaJoinedGsapScripts} from '@packages/systems/gsap-integration-settings';
import {IX3GsapScrollMarkerStyle} from '../../../public/js/designer-flux/IX3/components/canvas/IX3GsapScrollMarkerStyle';
import {pageComponentType} from '@packages/utilities/platform';
/* eslint-enable webflow/package-boundaries */

// Start: this is temporary duplication of code from public/js/designer-flux/components/Canvas.tsx
// Clean up epic: https://webflow.atlassian.net/browse/DESUX-8149
const values = weakMemo(Object.values);

const _collectHeadScripts = (
  headScripts: string[],
  atom: NormalizedAtomConfig
) =>
  atom.headScript != null && !headScripts.includes(atom.headScript)
    ? headScripts.concat(atom.headScript)
    : headScripts;

const getHeadScripts = weakMemo(
  (atoms: QualifiedMap<string, NormalizedAtomConfig>) =>
    atoms.reduce(_collectHeadScripts, [])
);
// End: this is temporary duplication of code from public/js/designer-flux/components/Canvas.tsx

// Apply document.write override immediately to ensure it's in place before any scripts run
overrideDocumentWrite();

// Make React's reconciler tolerant of GSAP pin-spacer wrap/unwrap of body-level
// nodes before <App/> renders and IX3 initializes (see patchReactPinResilience).
patchReactPinResilience();

const WEBFLOW_JS_SRC = `${getDesignerAppOrigin(window.location.origin)}/js/site/webflow.js`;

const onCanvasViewportPanned = (dx: number, dy: number) => {
  dispatchInDesigner(canvasViewportPanned({dx, dy}));
};

const onCanvasViewportChangeStarted = (
  actionType: ContinuousViewportChangeActionType,
  trigger: CanvasViewportEventTrigger
) => {
  dispatchInDesigner(
    canvasViewportChangeStarted({
      trigger,
      isTriggeredFromIframe: true,
      analytics: {
        actionType,
      },
    })
  );

  // Middle-mouse pan may not trigger iframe mouseleave, so clear hover
  // explicitly when a real pan starts.
  if (isPanTrigger(trigger)) {
    dispatchInDesigner({
      type: CANVAS_APP_NODE_HOVERED,
      payload: {nativeIdPath: []},
    });
  }
};

const onCanvasViewportChangeEnded = (
  _actionType: ContinuousViewportChangeActionType,
  trigger: CanvasViewportEventTrigger
) => {
  dispatchInDesigner(
    canvasViewportChangeEnded({
      trigger,
      analytics: {
        source: 'canvas',
      },
    })
  );
};

export const App = () => {
  const {
    iframeStyleSheetHref,
    components,
    pageId,
    fonts,
    assetState,
    typeKitFonts,
    typeKitId,
    ixState,
    slugMap,
    styleBlockState,
    cssVariablesState,
    customFonts,
    breakpointId,
    collections,
    betaFeatureState,
    customCodeSiteHead,
    customCodeSitePostBody,
    currentPage,
    timezone,
    uiNodeState,
    workflow,
    canvasScrollState,
    ix2Events,
    ix2ActionLists,
    isPageLoading,
    canvasResizeState,
    openCanvasViewport,
    siteBasePath,
    paginationPageNumbers,
    siteId,
    variantDataMap,
    externalCustomCode,
    annotationsState,
    librariesCapabilitiesMap,
    isUnauthorizedUserType,
    isAnonymousCommentUserType,
    textZoom,
    ecommCurrencySettings,
    databaseId,
    items,
    currentItem,
    publicationId,
    currentLocale,
    isIndexed,
    lastPublishedDomain,
    optimizationState,
    gsapScripts,
    interactions,
    timelines,
    entitlements,
    siteModuleDependencies,
    isElementStylesEnabled,
    isComponentCanvasActive,
    globalComponentIds,
    componentLocalizations,
    ...restCanvasBodyProps
  } = useReceivedProjectData();

  // Save the viewport object in a ref to read in callbacks without
  // introducing the object into the deps array
  const openCanvasViewportRef = React.useRef(openCanvasViewport);
  openCanvasViewportRef.current = openCanvasViewport;

  const onCanvasViewportZoomed = React.useCallback(
    (zoomFactor: number, originPoint: Point) => {
      if (!openCanvasViewportRef.current) {
        return;
      }

      const currentZoom = openCanvasViewportRef.current.k;
      if (!canApplyZoomFactor(currentZoom, zoomFactor)) {
        return;
      }

      dispatchInDesigner(canvasViewportZoomed({zoomFactor, originPoint}));
      return true;
    },
    []
  );

  const {apolloClient, isDynamoDataLoaded} = useDynamoData(
    siteId,
    isPageLoading,
    components
  );

  const pluginsRef = React.useRef<Plugins.PluginsType | undefined>(undefined);
  const [loadedHeadScripts, setLoadedHeadScripts] =
    React.useState(ImmSet<string>());
  const designerAppOrigin = getDesignerAppOrigin(window.location.origin);

  useDocumentAttributes(
    siteId,
    workflow,
    isUnauthorizedUserType,
    currentLocale?.tag,
    Boolean(openCanvasViewport),
    isAnonymousCommentUserType
  );
  useBaseSiteModules(components);
  const {areAllScriptsLoaded, scriptsPending} = useAreAllScriptsLoaded({
    customCodeSitePostBody,
    currentPage,
    customCodeSiteHead,
  });
  const isPageAndDynamoDataLoaded = !isPageLoading && isDynamoDataLoaded;
  const isWebflowJsLoaded =
    scriptsPending !== undefined && !scriptsPending.has(WEBFLOW_JS_SRC);
  const isLoading = !isPageAndDynamoDataLoaded || !isWebflowJsLoaded;
  useReportCanvasLatency(isLoading, workflow);

  React.useEffect(() => {
    if (!isLoading) {
      setCanvasAppReady();
    }
  }, [isLoading]);

  useVariationAttributes(optimizationState);

  useEnsureNewDocumentOnPageSwitch({pageId, isPageLoading});

  useReportCustomCodeErrors();

  useInitializeIX2OnPage({
    ix2Events,
    ix2ActionLists,
    styleBlocks: styleBlockState?.styleBlocks,
    slugMap,
    variables: cssVariablesState?.variables,
    variableCollections: cssVariablesState?.variableCollections,
    pageId,
    isLoading,
  });

  useInitializeIX3OnSeparateCanvasOrigin({
    interactions,
    timelines,
    isLoading,
    pageId,
  });

  useReportReceivedRequiredProjectData(
    {
      components,
      pageId,
      styleBlockState,
      cssVariablesState,
      assetState,
      fonts,
      slugMap,
      customFonts,
      typeKitFonts,
      currentPage,
    },
    'app'
  );

  useResizeObserver();

  useDesignModeHandler(designerAppOrigin);
  useReportCanvasLoadedWithWhiteScreen();
  useNotifyDesignerAppCanvasAppUnload();

  // Don't render anything until we receive all needed data to render everything that needs to go inside the head
  // as otherwise order of the things that will be rendered is no guaranteed and we want to preserve order for styles and scripts
  // to work correctly.
  if (
    components === undefined ||
    pageId === undefined ||
    styleBlockState === undefined ||
    cssVariablesState === undefined ||
    assetState === undefined ||
    fonts === undefined ||
    slugMap === undefined ||
    customFonts === undefined ||
    breakpointId === undefined ||
    typeKitFonts === undefined ||
    currentPage === undefined ||
    variantDataMap === undefined ||
    externalCustomCode === undefined ||
    annotationsState === undefined ||
    librariesCapabilitiesMap === undefined ||
    canvasScrollState === undefined ||
    canvasResizeState === undefined ||
    uiNodeState === undefined ||
    workflow === undefined ||
    ixState === undefined ||
    isPageLoading === undefined ||
    betaFeatureState === undefined ||
    collections === undefined ||
    items === undefined ||
    paginationPageNumbers === undefined ||
    entitlements === undefined
  ) {
    return null;
  }

  // Bootstrap plugins once we have all the data we need
  if (
    !pluginsRef.current &&
    betaFeatureState &&
    collections &&
    siteModuleDependencies
  ) {
    pluginsRef.current = initBootstrapPlugins(
      betaFeatureState,
      collections,
      timezone || 'UTC',
      siteModuleDependencies
    );
  }

  if (!pluginsRef.current) return null;

  const pluginComponents = Plugins.getPluginComponents(pluginsRef.current);
  const atoms = Plugins.getAtoms(pluginsRef.current);

  const onHeadScriptLoad = (win: Window, src: string) => {
    if (!loadedHeadScripts.has(src)) {
      setLoadedHeadScripts((loadedHeadScriptsState) =>
        loadedHeadScriptsState.add(src)
      );
    }
  };

  const itemId = currentItem ? getId(currentItem) : null;
  const cmsLocaleId = currentLocale?.cmsId;

  // Primary locale without a redirect (not enabled)
  // is assigned the root publish target
  const publishTarget =
    (currentLocale?.primary && !currentLocale?.enabled) || !currentLocale?._id
      ? lastPublishedDomain
      : `${lastPublishedDomain}/${currentLocale._id.toString()}`;

  return (
    <>
      <DynamoDataProvider
        apolloClient={apolloClient}
        collections={collections}
        databaseId={databaseId}
        items={items}
        itemId={itemId}
        page={currentPage}
        paginationPageNumbers={paginationPageNumbers}
        plugins={pluginsRef.current}
        publicationId={publicationId || ''}
        publishTarget={publishTarget}
        runtimeEnvironment={WebflowEnvironments.DESIGNER}
        showSampleSearchResults={!isIndexed}
        // @ts-expect-error - TODO - account for timezone possible null value properly
        timezone={timezone}
        cmsLocaleId={cmsLocaleId}
        // components cannot be focused for editing in Canvas App use cases
        // if the Canvas App is used for Designer rendering outside Preview
        // and Annotation modes, we need to pass in the actual path here
        focusedSymbolNativeIdPath={emptyArray}
        rootComponentType={pageComponentType}
      >
        {(dynamoData) => (
          <OpenCanvasProvider
            isActive={Boolean(openCanvasViewport)}
            htmlEmbedOpenCanvasRewriter={rewriteViewportUnitsInHtml}
          >
            <CanvasHead>
              <link
                rel="stylesheet"
                type="text/css"
                crossOrigin="anonymous"
                href={iframeStyleSheetHref}
              />
              {styleBlockState.breakpoints ? (
                <CanvasDefaultStyles
                  atoms={atoms}
                  componentMap={pluginComponents}
                  breakpoints={styleBlockState.breakpoints}
                />
              ) : null}
              {openCanvasViewport ? (
                <ViewportStyles
                  viewport={openCanvasViewport}
                  targetDocument={document}
                />
              ) : null}
              <IX3GsapScrollMarkerStyle />
              <style>{`html { font-size: ${textZoom}px; }`}</style>
              {gsapScripts
                ? parseCommaJoinedGsapScripts(gsapScripts)?.map((url) => (
                    <Script src={url} key={url} async={false} />
                  ))
                : null}
              <Script src={WEBFLOW_JS_SRC} async={false} />
              <CustomFonts fonts={customFonts} renderStyles />
              <TypeKitFontsScript fonts={typeKitFonts} typeKitId={typeKitId} />
              <GoogleFontsSheet fonts={fonts} />
              {atoms
                ? getHeadScripts(atoms).map((headScript: string) => (
                    <Script
                      src={headScript}
                      key={headScript}
                      onLoad={onHeadScriptLoad}
                    />
                  ))
                : null}
              <ErrorBoundary
                onError={(error) => {
                  telemetrySendError({
                    name: 'Stylesheet generation error',
                    error,
                  });
                }}
              >
                <CanvasStyleSheet
                  styleSheetImageResolver={assetState}
                  cssVariablesState={cssVariablesState}
                  isEphemeralChangeInProgress={false}
                  selectedNodeNativeId={null}
                  styleBlockState={styleBlockState}
                  styleSheetRendered={noop}
                  fonts={fonts}
                  slugMap={slugMap}
                  variantDataMap={variantDataMap}
                  renderStyles
                  isElementStylesEnabled={isElementStylesEnabled ?? false}
                />
                <CanvasLocalStyles
                  componentMap={components}
                  pageId={pageId}
                  breakpoints={styleBlockState.breakpoints}
                />
              </ErrorBoundary>
              <style>{getIXPreviewStyles(ixState)}</style>
              {
                // Forcing scroll behavior to be auto in the context of the separate-origin canvas.
                // Customers that added scroll-behavior: smooth to their custom code experienced problems
                // while working on the canvas in Preview mode and Custom code enabled.
                // Associated ticket: https://webflow.atlassian.net/browse/DESUX-8625
              }
              <style>{`html {scroll-behavior: auto !important;}`}</style>
              <ErrorBoundary
                onError={(error) => {
                  console.error('Custom code error:', error);
                  telemetrySendError({
                    name: 'Custom code head error',
                    error,
                  });
                }}
              >
                {/* Disable Optimize scripts in preview mode */}
                <HtmlFragment
                  fragment={`<script>(function(e){var s={r:[]};e.wf={r:s.r,ready:t=>{s.r.push(t); console.warn('Webflow browser APIs (via the wf object) are not supported in Preview Mode.');}}})(window)</script>`}
                  isReadyToBeInserted
                />
                <HtmlFragment
                  fragment={customCodeSiteHead}
                  isReadyToBeInserted={isWebflowJsLoaded}
                />
                <CurrentPageCustomCode
                  fragment={currentPage.head}
                  currentPage={currentPage}
                  timezone={timezone}
                  currencySettings={ecommCurrencySettings}
                  collections={collections}
                  betaFeatureState={betaFeatureState}
                  apolloClient={apolloClient}
                  isReadyToBeInserted={isWebflowJsLoaded}
                  dynamoData={dynamoData}
                />
                <ExternalCustomCode
                  externalCustomCode={externalCustomCode}
                  position="header"
                  pageId={pageId}
                  isReadyToBeInserted
                />
              </ErrorBoundary>
            </CanvasHead>
            <CanvasBodyWithDefaults
              {...restCanvasBodyProps}
              variables={cssVariablesState.variables}
              variableCollections={cssVariablesState.variableCollections}
              currentVariationId={optimizationState?.currentVariationId}
              siteId={siteId}
              siteBasePath={siteBasePath}
              collections={collections}
              components={components}
              pageId={pageId}
              assets={buildCanvasAssetMap(assetState.assets)(
                currentLocale?._id
              )}
              ixState={ixState}
              cssPropertyMap={styleBlockState?.cssPropertyMap}
              classNameMap={
                slugMap && styleBlockState
                  ? buildClassNameMap(slugMap)(styleBlockState.styleBlocks)
                  : undefined
              }
              timezone={timezone}
              plugins={pluginsRef.current!}
              ix3ElementTargets={
                interactions && timelines
                  ? stringifyIx3ElementTargets(values(interactions))(
                      values(timelines)
                    )
                  : defaultStringifiedTargets
              }
              ix2NodeMap={getIx2NodeMap(ix2Events)(ix2ActionLists)}
              paginationPageNumbers={paginationPageNumbers}
              loadedHeadScripts={loadedHeadScripts}
              variantDataMap={variantDataMap}
              stylesMap={buildStylesMapForCanvasApp(pluginsRef.current!)(
                cssVariablesState
              )(components)(styleBlockState)}
              styleBlocks={styleBlockState.styleBlocks}
              breakpoints={styleBlockState.breakpoints}
              breakpointId={breakpointId}
              apolloClient={apolloClient}
              // Canvas app has its own Apollo client that does not get
              // its cache reset by actions like changing collections or
              // publishing, so we don't need to supply a cache reset counter.
              // If this changes, we can implement the cache reset counter in
              // the useDynamoData hook that manages the apolloClient instance.
              apolloCacheResetCounter={0}
              canvasScrollState={canvasScrollState}
              canvasResizeState={canvasResizeState}
              isOpenCanvasActive={Boolean(openCanvasViewport)}
              uiNodeState={uiNodeState}
              workflow={workflow}
              databaseId={databaseId}
              currentItemId={itemId}
              publicationId={publicationId}
              currentLocale={currentLocale}
              isIndexed={isIndexed}
              dynamoData={dynamoData}
              ecommCurrencySettings={ecommCurrencySettings}
              entitlements={entitlements}
              isComponentCanvasActive={isComponentCanvasActive}
              globalComponentIds={globalComponentIds}
              componentLocalizations={
                componentLocalizations ?? QualifiedMap.empty()
              }
            />
            {openCanvasViewport && (
              <ViewportEventHandlers
                contentWindow={window}
                onCanvasViewportPanned={onCanvasViewportPanned}
                onCanvasViewportZoomed={onCanvasViewportZoomed}
                onCanvasViewportChangeStarted={onCanvasViewportChangeStarted}
                onCanvasViewportChangeEnded={onCanvasViewportChangeEnded}
              />
            )}
            <BodyEndCustomCode
              customCodeSitePostBody={customCodeSitePostBody}
              currentPage={currentPage}
              timezone={timezone}
              ecommCurrencySettings={ecommCurrencySettings}
              collections={collections}
              betaFeatureState={betaFeatureState}
              apolloClient={apolloClient}
              isReadyToBeInserted={!isLoading}
              dynamoData={dynamoData}
              externalCustomCode={externalCustomCode}
              pageId={pageId}
            />
            <DocumentEvents
              isLoading={isLoading}
              pageId={pageId}
              dynamoData={dynamoData}
              areAllScriptsLoaded={areAllScriptsLoaded}
            />
          </OpenCanvasProvider>
        )}
      </DynamoDataProvider>
    </>
  );
};
